import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  FileSpreadsheet,
  FileText,
  RotateCw,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Spinner } from "@/components/ui/v2/spinner";
import { isImageUrl, useImageLinkPreview } from "@/context/ImagePreview";
import { cn } from "@/utils/cn";
import { isHttpUrl } from "@/utils/file.utils";
import type { IMayaAttachment } from "@/hooks/useMayaFileUploads";

interface AttachmentChipsProps {
  attachments: IMayaAttachment[];
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
  size?: "sm" | "md";
  className?: string;
}

const isImage = (file: File) => file.type.startsWith("image/");
const isSpreadsheet = (name: string) => /\.(csv|xlsx|xls)$/i.test(name);
const isSpreadsheetUrl = (url: string) => /\.(csv|xlsx|xls)(\?.*)?$/i.test(url);

const ChipThumb = ({
  attachment,
  thumbSize,
}: {
  attachment: IMayaAttachment;
  thumbSize: number;
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage(attachment.file)) return;
    const url = URL.createObjectURL(attachment.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment.file]);

  const isUploading = attachment.status === "uploading";
  const isFailed = attachment.status === "failed";
  const showImage = previewUrl && !isFailed;

  return (
    <div
      className={cn(
        "relative shrink-0 rounded-xs overflow-hidden border flex items-center justify-center",
        isFailed
          ? "bg-danger-muted border-danger/40"
          : "bg-muted border-border",
      )}
      style={{ width: thumbSize, height: thumbSize }}
    >
      {showImage ? (
        <img
          src={previewUrl}
          alt={attachment.name}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : isFailed ? (
        <AlertCircle className="size-3.5 text-danger" />
      ) : isSpreadsheet(attachment.name) ? (
        <FileSpreadsheet className="size-3.5 text-success" />
      ) : (
        <FileText className="size-3.5 text-muted-foreground" />
      )}
      {isUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <Spinner
            size="sm"
            className="text-white"
            bgClassName="stroke-white/30"
          />
        </div>
      )}
    </div>
  );
};

export const AttachmentChips = ({
  attachments,
  onRemove,
  onRetry,
  size = "md",
  className,
}: AttachmentChipsProps) => {
  const { t } = useTranslation(["common"]);
  if (attachments.length === 0) return null;

  const thumbSize = size === "sm" ? 32 : 40;

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {attachments.map((a) => (
        <div
          key={a.id}
          className="relative shrink-0"
          style={{ width: thumbSize, height: thumbSize }}
          title={a.error ?? a.name}
        >
          <ChipThumb attachment={a} thumbSize={thumbSize} />
          <button
            type="button"
            onClick={() => onRemove(a.id)}
            aria-label={t("common:ai_assistant.remove_file", { name: a.name })}
            className="absolute -top-1.5 -end-1.5 size-4 flex items-center justify-center rounded-full bg-foreground text-surface shadow-xs hover:bg-foreground/80 transition-colors"
          >
            <X className="size-2.5" />
          </button>
          {a.status === "failed" && onRetry && (
            <button
              type="button"
              onClick={() => onRetry(a.id)}
              aria-label={t("common:ai_assistant.retry_uploading", {
                name: a.name,
              })}
              className="absolute -bottom-1.5 -end-1.5 size-4 flex items-center justify-center rounded-full bg-danger text-danger-foreground shadow-xs hover:bg-danger/90 transition-colors"
            >
              <RotateCw className="size-2.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

interface MessageAttachment {
  name: string;
  url: string;
}

interface BubbleAttachmentsProps {
  attachments: MessageAttachment[];
  variant: "user" | "assistant";
  size?: "sm" | "md";
  align?: "start" | "end";
  className?: string;
}

export const BubbleAttachments = ({
  attachments,
  variant,
  size = "md",
  align = "start",
  className,
}: BubbleAttachmentsProps) => {
  const previewOnClick = useImageLinkPreview();
  // Links and images only ever point at http(s) — never javascript:/data:.
  const safe = attachments?.filter((a) => isHttpUrl(a.url)) ?? [];
  if (!safe.length) return null;
  const textSize = size === "sm" ? "text-[10px]" : "text-xs";
  const chipHeight = size === "sm" ? "h-6" : "h-7";
  const justifyClass = align === "end" ? "justify-end" : "justify-start";

  const images = safe.filter((a) => isImageUrl(a.url));
  const others = safe.filter((a) => !isImageUrl(a.url));
  const imageUrls = images.map((a) => a.url);

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5",
        align === "end" ? "items-end" : "items-start",
        className,
      )}
    >
      {images.length > 0 && (
        <div className={cn("flex flex-wrap gap-1.5", justifyClass)}>
          {images.map((a, i) => (
            <a
              key={a.url}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => previewOnClick(e, imageUrls, i)}
              className={cn(
                "block rounded-md overflow-hidden border hover:opacity-90 transition-opacity cursor-zoom-in",
                variant === "user" ? "border-white/30" : "border-border",
              )}
              title={a.name}
            >
              <img
                src={a.url}
                alt={a.name}
                className="max-h-32 max-w-[180px] object-cover"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div className={cn("flex flex-wrap gap-1.5", justifyClass)}>
          {others.map((a) => (
            <a
              key={a.url}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "flex items-center gap-1.5 px-2 rounded-md border font-medium transition-colors",
                chipHeight,
                textSize,
                variant === "user"
                  ? "border-white/30 bg-white/10 text-primary-foreground hover:bg-white/20"
                  : "border-border bg-surface text-foreground hover:bg-muted",
              )}
              title={a.name}
            >
              {isSpreadsheetUrl(a.url) ? (
                <FileSpreadsheet className="size-3.5 shrink-0" />
              ) : (
                <FileText className="size-3.5 shrink-0" />
              )}
              <span className="truncate max-w-[140px]">{a.name}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
